<script lang="ts">
  import { onDestroy, onMount, type Snippet } from 'svelte';
  import { on } from 'svelte/events';
  import Card from '../ui/Card.svelte';
  import BoardTile from './BoardTile.svelte';
  import { advanceTrace, type EncounteredPickup } from './board-trace';
  import TracePath from './TracePath.svelte';
  import type { BoardSize } from '../../shared/config';
  import type { PowerUpSpawn } from '../../shared/power-ups';
  import { Sfx } from '../audio/sounds';
  import { boardIndexAt, crossedBoardTiles, type Point } from './board-hit-testing';
  import { foggedTiles, spunTile } from './chaos';

  interface Props {
    board: readonly string[] | null;
    size: BoardSize;
    revealed: boolean;
    enabled: boolean;
    /** Bound by the room UI for hints, round resets, and the current-word display. */
    selectedPath?: number[];
    /** The trace is submitted with the word so the server can credit chaos pickups. */
    submit: (word: string, path: readonly number[], pickupIds: readonly string[]) => void;
    observeInput?: ((timestamp: number) => void) | undefined;
    header?: Snippet;
    /** Chaos presentation; letters, geometry and rules never change per player. */
    powerUps?: readonly Readonly<PowerUpSpawn>[];
    spun?: boolean;
    fogged?: boolean;
    frozen?: boolean;
  }
  let {
    board,
    size,
    revealed,
    enabled,
    selectedPath = $bindable([]),
    submit,
    observeInput = undefined,
    header,
    powerUps = [],
    spun = false,
    fogged = false,
    frozen = false,
  }: Props = $props();

  let boardElement: HTMLDivElement;
  let activePointerId: number | null = null;
  let pickups: readonly EncounteredPickup[] = [];
  let lastPoint: Point | null = null;
  let feedback: 'select' | 'backtrack' | null = null;
  let revealing = $state(false);
  let revealTimer: ReturnType<typeof setTimeout>;
  let dragBounds: DOMRect | null = null;
  let boardWidth = $state(0);
  onMount(() => {
    const invalidate = () => {
      if (activePointerId !== null) cancelGesture();
    };
    const resize = new ResizeObserver(([entry]) => {
      // The observer's initial notification may arrive after pointerdown.
      // Cancel only when the cached coordinate frame actually changed.
      if (dragBounds) {
        const current = boardElement.getBoundingClientRect();
        if (
          current.left !== dragBounds.left ||
          current.top !== dragBounds.top ||
          current.width !== dragBounds.width ||
          current.height !== dragBounds.height
        )
          invalidate();
      }
      // Container-query font sizes caused every animated frame to invalidate
      // all tiles. Only update font metrics when the board actually resizes.
      if (entry) boardWidth = entry.contentRect.width;
    });
    resize.observe(boardElement);
    // iOS Safari runs a long-press-then-pan text-selection gesture alongside pointer events;
    // neither `touch-action: none`, `user-select: none` nor `pointerdown.preventDefault()`
    // stop it (WebKit 207140). Only a non-passive touch listener does, and Svelte's
    // `ontouchstart` attribute is passive by design.
    const suppressNativeTouch = (event: TouchEvent) => {
      if (event.cancelable) event.preventDefault();
    };
    const offTouchStart = on(boardElement, 'touchstart', suppressNativeTouch, { passive: false });
    const offTouchMove = on(boardElement, 'touchmove', suppressNativeTouch, { passive: false });
    window.addEventListener('resize', invalidate);
    window.addEventListener('scroll', invalidate, true);
    return () => {
      offTouchStart();
      offTouchMove();
      resize.disconnect();
      window.removeEventListener('resize', invalidate);
      window.removeEventListener('scroll', invalidate, true);
    };
  });
  function animateReveal(value: boolean): void {
    clearTimeout(revealTimer);
    revealing = value;
    if (value) revealTimer = setTimeout(() => (revealing = false), 700);
  }
  $effect(() => animateReveal(revealed));
  onDestroy(() => clearTimeout(revealTimer));

  const cells = $derived(Array.from({ length: size * size }, (_, index) => board?.[index] ?? ''));
  const spawnByTile = $derived(new Map(powerUps.map((spawn) => [spawn.tile, spawn.kind])));
  const fog = $derived(fogged ? foggedTiles(selectedPath[selectedPath.length - 1], size) : null);
  /** Pointer geometry is computed on the unrotated grid; a spun board mirrors the index. */
  const tileUnderPointer = (index: number) => (spun ? spunTile(index, size) : index);
  $effect(() => {
    if (!enabled) cancelGesture();
  });
  // Equivalent room snapshots must not interrupt an in-flight gesture: derive a value
  // key so only a genuinely different board resets the gesture.
  const boardKey = $derived(JSON.stringify([size, board]));
  $effect(() => {
    void boardKey;
    reset();
  });

  function cancelGesture(): void {
    const pointerId = activePointerId;
    activePointerId = null;
    lastPoint = null;
    dragBounds = null;
    feedback = null;
    pickups = [];
    selectedPath = [];
    if (pointerId !== null && boardElement?.hasPointerCapture(pointerId)) {
      boardElement.releasePointerCapture(pointerId);
    }
  }

  export function reset(): void {
    cancelGesture();
  }

  function point(event: PointerEvent): Point {
    return { x: event.clientX, y: event.clientY };
  }

  function playFeedback(): void {
    // One meaningful cue per delivered event, never a queued burst for inferred tiles.
    if (feedback) Sfx.play(feedback);
    feedback = null;
  }

  function addTile(index: number): void {
    const next = advanceTrace(selectedPath, pickups, index, size, powerUps);
    if (!next) return;
    selectedPath = next.path;
    pickups = next.pickups;
    feedback = next.feedback;
  }

  function pointerDown(event: PointerEvent): void {
    if (activePointerId !== null || !event.isPrimary || !enabled) return;
    observeInput?.(event.timeStamp);
    event.preventDefault();
    Sfx.unlock();
    pickups = [];
    selectedPath = [];
    dragBounds = boardElement.getBoundingClientRect();
    activePointerId = event.pointerId;
    lastPoint = point(event);
    try {
      boardElement.setPointerCapture(event.pointerId);
    } catch {
      /* Synthetic events and stale pointers may not support capture. */
    }
    addTile(tileUnderPointer(boardIndexAt(lastPoint, dragBounds, size)));
    playFeedback();
  }

  function moveThrough(event: PointerEvent): void {
    if (!dragBounds || !lastPoint) return;
    const samples = event.getCoalescedEvents?.() ?? [];
    for (const sample of [...samples, event]) {
      const next = point(sample);
      for (const index of crossedBoardTiles(lastPoint, next, dragBounds, size))
        addTile(tileUnderPointer(index));
      lastPoint = next;
    }
    playFeedback();
  }

  function pointerMove(event: PointerEvent): void {
    if (event.pointerId !== activePointerId || !enabled) return;
    observeInput?.(event.timeStamp);
    moveThrough(event);
  }

  function pointerUp(event: PointerEvent): void {
    if (event.pointerId !== activePointerId || !enabled) return;
    moveThrough(event);
    const word = selectedPath.map((index) => board?.[index] ?? '').join('');
    const path = selectedPath;
    const pickupIds = pickups.map((pickup) => pickup.id);
    cancelGesture();
    if (word) submit(word, path, pickupIds);
  }

  function pointerCancel(event: PointerEvent): void {
    if (event.pointerId === activePointerId) cancelGesture();
  }
</script>

<Card class={`board-card${header ? ' has-header' : ''}`}>
  {#if header}<div class="board-header">{@render header()}</div>{/if}
  <div class="board-wrap" class:spun class:frozen>
    <div
      bind:this={boardElement}
      role="application"
      aria-label="letter board"
      class="board"
      style={`--board-size:${size};--tile-font-size:${Math.min(46, (boardWidth * 0.6) / size)}px;--qu-font-size:${Math.min(38, (boardWidth * 0.48) / size)}px`}
      onpointerdown={pointerDown}
      onpointermove={pointerMove}
      onpointerup={pointerUp}
      onpointercancel={pointerCancel}
      onlostpointercapture={pointerCancel}
      oncontextmenu={(event) => event.preventDefault()}
    >
      {#each cells as letter, index (index)}
        <BoardTile
          {index}
          {letter}
          {revealed}
          {revealing}
          selected={selectedPath.includes(index)}
          head={selectedPath[selectedPath.length - 1] === index}
          powerUp={spawnByTile.get(index)}
          fogged={fog?.[index] ?? false}
        />
      {/each}
    </div>
    <TracePath path={selectedPath} {size} />
  </div>
</Card>

<style>
  :global(.card.board-card) {
    position: relative;
    --board-header-space: 0px;
    --board-padding-inline: clamp(12px, 3vw, 18px);
    --board-padding-block: clamp(26px, calc(4dvh + 2px), 38px);
    align-items: center;
    flex: 0 0 auto;
    padding: var(--board-padding-block) var(--board-padding-inline);
  }
  :global(.card.board-card.has-header) {
    --board-header-space: calc(var(--word-size, 36px) * 1.2 + 32px);
    gap: 32px;
  }
  .board-header {
    width: 100%;
    height: calc(var(--board-header-space) - 32px);
    flex: 0 0 auto;
  }
  .board-wrap {
    position: relative;
    flex: 0 0 auto;
    /* Scroll the game area rather than shrinking touch targets on short screens. */
    width: min(
      100%,
      460px,
      max(
        224px,
        min(
          50dvh,
          calc(
            100cqh - var(--stack-overhead) - 2 * var(--board-padding-block) - var(--board-header-space) - 2px
          )
        )
      )
    );
    min-width: 0;
    min-height: 0;
    aspect-ratio: 1;
  }
  /* Chaos: a spun board is upside down, letters included; the pointer mapping follows. */
  .board-wrap {
    transition: transform 400ms ease;
  }
  .board-wrap.spun {
    transform: rotate(180deg);
  }
  /* Frozen input is refused by the server; the overlay explains why the board ignores the finger. */
  .board-wrap.frozen::after {
    content: '❄';
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: clamp(48px, 20cqw, 96px);
    color: var(--theme-accent);
    background: color-mix(in srgb, var(--theme-surface) 55%, transparent);
    border-radius: var(--theme-tile-radius);
    pointer-events: none;
  }
</style>
